import { Component, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { distinctUntilChanged, firstValueFrom } from "rxjs";

import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { CollectionService } from "@bitwarden/common/abstractions/collection.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { OrganizationService } from "@bitwarden/common/abstractions/organization/organization.service.abstraction";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { SyncService } from "@bitwarden/common/abstractions/sync/sync.service.abstraction";
import { CipherService } from "@bitwarden/common/abstractions/cipher.service";
import { Utils } from "@bitwarden/common/misc/utils";
import { Organization } from "@bitwarden/common/models/domain/organization";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { ConfirmationDialogComponent } from "@bitwarden/web-vault/app/components/confirmation-dialog/confirmation-dialog.component";
import {
  DialogWrapperComponent,
  DialogWrapperData,
} from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { CreateCollectionComponent } from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/create-collection.component";
import { formatRelativeTime } from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/relative-time";
import {
  buildTeamAccountsLookup,
  TeamAccountsLookup,
} from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/team-accounts";
import { TeamDetailComponent } from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/team-detail.component";
import {
  TeamInvitationResponse,
  TeamInvitationService,
} from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/team-invitation.service";
import {
  classifyInviteFailure,
  inviteErrorMessage,
} from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/team-invitation-errors";
import {
  INVITATION_NOT_APPROVED_BADGE_CLASS,
  TEAM_STATUS_BADGE_CLASS,
  TEAM_STATUS_LABEL_KEY,
  TeamStatusKey,
} from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/team-status";
import { GlossRouteEnum } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { RIGHT_PANEL_CONFIG } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { RightPanelService } from "@bitwarden/web-vault/app/components/dialog/right-panel.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";

export type TeamsTab = "myTeams" | "invitations";

interface TeamRow {
  collectionId: string;
  org: Organization;
  name: string;
  subtitle: string;
  sharedLabel: string;
  badgeKey: TeamStatusKey;
}

type InvitationBadgeKey = TeamStatusKey | "notApproved";

interface InvitationRow {
  // Organization invites are unique per org, collection invites per collection, rejections per
  // rejection record.
  id: string;
  invitation: TeamInvitationResponse;
  name: string;
  subtitle: string;
  badgeKey: InvitationBadgeKey;
}

// Routed at GlossRoutes.Collections. "My teams" lists every collection the current user can
// decrypt across every org they have settled access to (their own org, plus any other org
// they've been confirmed into) — a collection is what the product calls a "team". "Invitations"
// lists every pending invite from GET /organizations/invitations: a first team in an org the
// user hasn't joined yet, or one more team in an org they already belong to. Sync can't be used
// for this, since it only returns Confirmed memberships and knows nothing of team-level invites.
@Component({
  selector: "app-organization-page",
  templateUrl: "./organization-page.component.html",
})
export class OrganizationPageComponent {
  private readonly rightPanel = inject(RightPanelService);
  private readonly organizationService = inject(OrganizationService);
  private readonly collectionService = inject(CollectionService);
  private readonly logService = inject(LogService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly platformUtilsService = inject(PlatformUtilsService);
  private readonly i18nService = inject(I18nService);
  private readonly userStore = inject(UserStoreService);
  private readonly teamInvitationService = inject(TeamInvitationService);
  private readonly syncService = inject(SyncService);
  private readonly cipherService = inject(CipherService);

  readonly organizations$ = this.organizationService.organizations$;
  readonly badgeClass = TEAM_STATUS_BADGE_CLASS;
  readonly invitationBadgeClass: Record<InvitationBadgeKey, string> = {
    ...TEAM_STATUS_BADGE_CLASS,
    notApproved: INVITATION_NOT_APPROVED_BADGE_CLASS,
  };
  readonly invitationBadgeLabelKey: Record<InvitationBadgeKey, string> = {
    ...TEAM_STATUS_LABEL_KEY,
    notApproved: "statusNotApproved",
  };
  readonly badgeLabelKey = TEAM_STATUS_LABEL_KEY;

  activeTab: TeamsTab = "myTeams";

  // organizations$ is a BehaviorSubject seeded with [] that only fills in once the on-disk
  // state is read, so it emits [] on first render even for a user who has orgs. getAll() is
  // that same on-disk read; gating the empty-state on it avoids flashing "no teams" while the
  // read is still in flight.
  orgsLoaded = false;
  isLoadingTeams = false;
  teamRows: TeamRow[] = [];
  isLoadingInvitations = false;
  invitationRows: InvitationRow[] = [];
  // Row ids with an accept/decline request in flight, so a double click can't send it twice.
  pendingInvitationIds = new Set<string>();
  // Invites whose accept / decline the backend rejected for good (a non-retryable 4xx): that
  // button is hidden for this visit, since every retry would fail the same way. Keyed by action
  // and row id (see blockKey), not the token: gloss-back mints a new token for every row on every
  // list load, so a token in the key let any reload bring the button back to fail again (#6259),
  // and a resend fixes none of the permanent causes (2FA, single-org policy, revoked access, a
  // deleted team). Each maps to the i18n key of why: shown inline once both a row's buttons are
  // blocked, as it has no actions left and only earlier toasts said why (#6260).
  private blockedActions = new Map<string, string>();
  // Rows accepted or declined this visit, each with what it became (null: gone) and the
  // invitationLoadSeq when it did. A reload started before then resolves with the server's
  // earlier snapshot, which mustn't bring the row back as still Invited (#6260).
  private settledInvitations = new Map<string, { seq: number; row: InvitationRow | null }>();
  // Rejections already announced this visit, so reloading the list doesn't toast them again.
  private announcedRejectionIds = new Set<string>();

  createTeamButton = new ActionButton({
    fullClass:
      "tw-inline-flex tw-h-10 tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-2xl tw-bg-color-action-primary tw-px-6 tw-text-sm tw-font-semibold tw-text-color-text-on-action tw-shadow-elevation-card sm:tw-w-full",
    ghostShadow: true,
    text: this.i18nService.t("createTeam"),
    onClick: this.openCreateTeamDialog.bind(this),
  });

  private latestOrgs: Organization[] = [];
  private loadSeq = 0;
  private invitationLoadSeq = 0;
  // The latest loadInvitations(), for an earlier one to resolve with (see there).
  private latestInvitationLoad: Promise<boolean> = Promise.resolve(true);
  // Late callbacks (a drawer's afterClosed after navigating away, a post-accept sync) mustn't
  // reload this page's data or toast its errors over whatever page the user is on now.
  private destroyed = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => (this.destroyed = true));

    void this.organizationService
      .getAll()
      .catch((e) => this.logService.error(String(e)))
      .finally(() => {
        this.orgsLoaded = true;
      });

    this.organizations$
      .pipe(
        distinctUntilChanged(
          (a, b) =>
            a.length === b.length &&
            a.every((o, i) => o.id === b[i].id && o.status === b[i].status),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((orgs) => {
        this.latestOrgs = orgs;
        void this.refresh(orgs);
      });

    void this.loadInvitations();
  }

  setTab(tab: TeamsTab) {
    this.activeTab = tab;
  }

  async acceptInvitation(row: InvitationRow) {
    if (this.pendingInvitationIds.has(row.id)) {
      return;
    }
    this.pendingInvitationIds.add(row.id);

    try {
      await this.teamInvitationService.accept(row.invitation);
    } catch (e) {
      this.logService.error(inviteErrorMessage(e));
      this.pendingInvitationIds.delete(row.id);
      this.handleInviteFailure(row, e, "accept");
      return;
    }

    this.pendingInvitationIds.delete(row.id);
    if (row.invitation.type === "Organization") {
      // Still waits for an admin to confirm the membership before the team shows up under My
      // teams, so the row stays, now as Awaiting.
      row.invitation.status = "Accepted";
      row.invitation.acceptedAt = new Date();
      const accepted = this.toInvitationRow(row.invitation);
      this.settleInvitation(row.id, accepted);
      this.invitationRows = this.invitationRows.map((r) => (r.id === row.id ? accepted : r));
      this.platformUtilsService.showToast(
        "success",
        this.i18nService.t("inviteAccepted"),
        this.acceptedAwaitingMessage(row.name, row.invitation.invitedByEmail),
      );
    } else {
      // A team invite grants access straight away.
      this.settleInvitation(row.id, null);
      this.invitationRows = this.invitationRows.filter((r) => r.id !== row.id);
      this.platformUtilsService.showToast("success", "", this.i18nService.t("inviteAccepted"));
    }
    // Pulls the newly accessible collection (and its key) into local state. Rebuild My teams
    // explicitly: a team invite doesn't change the org list, so organizations$ won't re-emit.
    await this.syncService
      .fullSync(true)
      .catch((e) => this.logService.error(inviteErrorMessage(e)));
    await this.refresh(this.latestOrgs);
  }

  declineInvitation(row: InvitationRow) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      panelClass: "confirmation-dialog",
      data: {
        title: "declineThisInvitation",
        type: "warning",
        // The dialog renders its message as HTML and the name is free text from the inviter.
        message: this.i18nService.t("declineInvitationConfirmation", Utils.escapeHtml(row.name)),
        customizedButton: {
          yes: { text: "decline", class: "primary" },
          no: { text: "keepInvitation", class: "neutral" },
        },
      },
    });
    void firstValueFrom(dialogRef.afterClosed()).then(async (confirmed) => {
      if (!confirmed || this.pendingInvitationIds.has(row.id)) {
        return;
      }
      this.pendingInvitationIds.add(row.id);

      try {
        await this.teamInvitationService.decline(row.invitation);
        // If the user has navigated away meanwhile, don't toast over the page they're on now. An
        // org invite no longer Invited comes back "Invitation not found" (gloss-back#98), which
        // handleInviteFailure drops and reloads like any invite no longer pending.
        if (this.destroyed) {
          return;
        }
        this.settleInvitation(row.id, null);
        this.invitationRows = this.invitationRows.filter((r) => r.id !== row.id);
        this.platformUtilsService.showToast(
          "success",
          "",
          this.i18nService.t("invitationDeclined"),
        );
      } catch (e) {
        this.logService.error(inviteErrorMessage(e));
        this.handleInviteFailure(row, e, "decline");
      } finally {
        this.pendingInvitationIds.delete(row.id);
      }
    });
  }

  // Cancelled, revoked or already accepted: no longer an invite to act on. Drop it now, then
  // reload: an org invite already accepted elsewhere comes back as Awaiting.
  private dropStaleInvitation(row: InvitationRow) {
    this.invitationRows = this.invitationRows.filter((r) => r.id !== row.id);
    void this.loadInvitations();
  }

  // Same classification as the accept-team page, so a stale or permanently rejected invite isn't
  // left with a button that can only fail again.
  private handleInviteFailure(row: InvitationRow, e: unknown, action: "accept" | "decline") {
    // A late failure after navigating away mustn't toast or reload over the page the user is on.
    if (this.destroyed) {
      return;
    }
    const failure = classifyInviteFailure(e, action);
    switch (failure.kind) {
      case "noLongerPending":
        this.dropStaleInvitation(row);
        break;
      case "superseded":
        // Resent since this list loaded (or its token expired, a tab left open past
        // invitation_expiration_hours, #6258): reloading picks up a fresh token, so trying again
        // works. accept-team's messages point at this very page, so say that instead.
        // Only once the reload has gone through: a failed one already toasts unexpectedError.
        void this.loadInvitations().then((reloaded) => {
          if (reloaded && !this.destroyed) {
            this.platformUtilsService.showToast(
              "error",
              "",
              this.i18nService.t(
                failure.key === "inviteLinkExpired"
                  ? "inviteExpiredRefreshed"
                  : "inviteResentRefreshed",
              ),
            );
          }
        });
        return;
      case "permanent":
        this.blockedActions.set(this.blockKey(row, action), failure.key);
        break;
    }
    this.platformUtilsService.showToast("error", "", this.i18nService.t(failure.key));
  }

  isActionBlocked(row: InvitationRow, action: "accept" | "decline"): boolean {
    return this.blockedActions.has(this.blockKey(row, action));
  }

  /** Why a row's Accept and Decline are both blocked (i18n keys, Accept's first), else none. */
  blockedReasons(row: InvitationRow): string[] {
    if (!this.isActionBlocked(row, "accept") || !this.isActionBlocked(row, "decline")) {
      return [];
    }
    return (["accept", "decline"] as const).flatMap(
      (action) => this.blockedActions.get(this.blockKey(row, action)) ?? [],
    );
  }

  private settleInvitation(rowId: string, row: InvitationRow | null) {
    this.settledInvitations.set(rowId, { seq: this.invitationLoadSeq, row });
  }

  private blockKey(row: InvitationRow, action: "accept" | "decline"): string {
    return `${action}|${row.id}`;
  }

  // Resolves with whether the list on screen is now fresh: false if this load failed, or, once a
  // newer load has started, with that load's outcome, since this one's result was discarded and a
  // caller mustn't claim the list was refreshed while the newer one may still fail (#6259).
  private loadInvitations(): Promise<boolean> {
    const seq = ++this.invitationLoadSeq;
    return (this.latestInvitationLoad = this.fetchInvitations(seq).then((ok) =>
      seq === this.invitationLoadSeq ? ok : this.latestInvitationLoad,
    ));
  }

  // A failed accept/decline can start a reload while another is in flight: only the latest one
  // may write the list, toast or end isLoadingInvitations, and none may once the page is gone.
  private async fetchInvitations(seq: number): Promise<boolean> {
    this.isLoadingInvitations = true;
    try {
      this.showInvitations(await this.teamInvitationService.getInvitations(), seq);
      return true;
    } catch (e) {
      this.logService.error(inviteErrorMessage(e));
      if (seq === this.invitationLoadSeq && !this.destroyed) {
        this.platformUtilsService.showToast("error", "", this.i18nService.t("unexpectedError"));
      }
      return false;
    } finally {
      if (seq === this.invitationLoadSeq) {
        this.isLoadingInvitations = false;
      }
    }
  }

  /** Shows a fetched list, unless a newer load has started since `seq` or the page is gone. */
  private showInvitations(invitations: TeamInvitationResponse[], seq: number) {
    if (seq !== this.invitationLoadSeq || this.destroyed) {
      return;
    }
    // Rows settled after this load started (its seq) keep what they became here; a load started
    // after that has the server's own word on them.
    const rows = invitations.map((invitation) => this.toInvitationRow(invitation));
    this.invitationRows = rows.flatMap((row) => {
      const settled = this.settledInvitations.get(row.id);
      if (!settled || settled.seq < seq) {
        return [row];
      }
      return settled.row ? [settled.row] : [];
    });
    // Settled before this load started: this list, and any later one, already has them.
    for (const [rowId, settled] of this.settledInvitations) {
      if (settled.seq < seq) {
        this.settledInvitations.delete(rowId);
      }
    }
    this.announceRejections(this.invitationRows);
  }

  // A rejection is shown to the invitee once: the row (and its tip) for this visit, plus a
  // notice. Marking it seen drops it from their next Invitations list.
  private announceRejections(rows: InvitationRow[]) {
    for (const row of rows) {
      const rejectionId = row.invitation.rejectionId;
      if (
        row.invitation.status !== "Rejected" ||
        !rejectionId ||
        this.announcedRejectionIds.has(rejectionId)
      ) {
        continue;
      }
      this.announcedRejectionIds.add(rejectionId);
      this.platformUtilsService.showToast(
        "info",
        this.i18nService.t("invitationClosed"),
        this.i18nService.t(
          "inviteNotConfirmedFor",
          row.invitation.invitedByEmail || this.i18nService.t("teamOwner"),
          row.name,
        ),
      );
      void this.teamInvitationService
        .markRejectionSeen(rejectionId)
        .catch((e) => this.logService.error(inviteErrorMessage(e)));
    }
  }

  private acceptedAwaitingMessage(teamName: string, inviterEmail: string | null): string {
    return inviterEmail
      ? this.i18nService.t("inviteAcceptedJoined", teamName, inviterEmail)
      : this.i18nService.t("inviteAcceptedJoinedOwner", teamName);
  }

  private toInvitationRow(invitation: TeamInvitationResponse): InvitationRow {
    const isCollection = invitation.type === "Collection";
    const invitedBy = invitation.invitedByEmail
      ? this.i18nService.t("invitedByEmail", invitation.invitedByEmail)
      : "";
    let subtitle: string;
    let badgeKey: InvitationBadgeKey;
    if (invitation.status === "Accepted") {
      subtitle = [
        invitation.invitedByEmail,
        this.i18nService.t("youAcceptedAgo", this.formatInvitedAt(invitation.acceptedAt)).trim(),
      ]
        .filter(Boolean)
        .join(" · ");
      badgeKey = "awaiting";
    } else if (invitation.status === "Rejected") {
      subtitle = [invitation.invitedByEmail, this.i18nService.t("didNotConfirmYou")]
        .filter(Boolean)
        .join(" · ");
      badgeKey = "notApproved";
    } else {
      subtitle = [invitedBy, this.formatInvitedAt(invitation.invitedAt)]
        .filter(Boolean)
        .join(" · ");
      badgeKey = "invited";
    }
    return {
      id:
        invitation.status === "Rejected"
          ? `rejection:${invitation.rejectionId}`
          : isCollection
            ? `collection:${invitation.collectionId}`
            : `organization:${invitation.organizationId}`,
      invitation,
      // Name the team, using the plaintext name the inviter sent (the collection's own name is
      // encrypted). Invites that didn't carry one fall back to the org's name.
      name:
        (isCollection ? invitation.collectionName : invitation.teamName) ||
        invitation.organizationName ||
        "",
      subtitle,
      badgeKey,
    };
  }

  private formatInvitedAt(date: Date | null): string {
    return formatRelativeTime(date, this.i18nService);
  }

  openCreateTeamDialog() {
    // The team goes into the org the user owns; with none yet (even if they're a member of
    // someone else's team), CreateCollectionComponent creates their own org first.
    const ownedOrg = this.latestOrgs.find((o) => o.isOwner);
    const dialogRef = this.rightPanel.open(DialogWrapperComponent, {
      data: {
        title: "createTeam",
        showBackButton: false,
        component: CreateCollectionComponent,
        componentInputs: {
          org: ownedOrg,
          onCreated: () => {
            dialogRef.close();
            void this.refresh(this.latestOrgs);
          },
        },
        fullyCustomised: true,
        showCloseButton: true,
      },
    });
  }

  openTeamDetail(row: TeamRow) {
    const dialogData: DialogWrapperData = {
      // The team's own name, not an i18n key.
      title: "",
      rawTitle: row.name,
      showBackButton: false,
      showCloseButton: true,
      component: TeamDetailComponent,
      componentInputs: {
        org: row.org,
        collectionId: row.collectionId,
        collectionName: row.name,
        // The header follows a rename, and the list is refreshed too: a rename finished by
        // closing the drawer lands after the close-time refresh below.
        onRenamed: (name: string) => {
          dialogData.rawTitle = name;
          void this.refresh(this.latestOrgs);
        },
        onDeleted: (): void => dialogRef.close(),
        onMembersChanged: (): void => void this.refresh(this.latestOrgs),
        onNavigateToAccounts: (accountId: string) => {
          dialogRef.close();
          // Same deep-link contract SearchBarComponent uses for its account results —
          // ManageAccountsComponent reads this query param itself and auto-selects the row.
          void this.router.navigate([GlossRouteEnum.account], {
            queryParams: { accountId },
          });
        },
      },
    };
    // The callbacks above only run once the drawer is open, by which time dialogRef is set.
    // Updating dialogData.rawTitle updates the header: the wrapper renders from this object.
    const dialogRef = this.dialog.open(DialogWrapperComponent, {
      ...RIGHT_PANEL_CONFIG,
      data: dialogData,
    });
    // A final catch-all in case a members change fires while a refresh from a previous change
    // is still in flight — the row data is a snapshot taken at open time, so the list needs a
    // refresh once closed regardless.
    void firstValueFrom(dialogRef.afterClosed()).then(() => this.refresh(this.latestOrgs));
  }

  private async refresh(orgs: Organization[]) {
    if (this.destroyed) {
      return;
    }
    const seq = ++this.loadSeq;
    this.isLoadingTeams = true;

    const accessibleOrgs = orgs.filter((o) => o.canAccess);

    if (accessibleOrgs.length === 0) {
      if (seq === this.loadSeq) {
        this.teamRows = [];
        this.isLoadingTeams = false;
      }
      return;
    }

    try {
      const allCollections = await this.collectionService.getAllDecrypted();
      if (seq !== this.loadSeq) {
        return; // superseded by a load for a different org list
      }

      const orgsById = new Map(accessibleOrgs.map((o) => [o.id, o]));
      const relevant = allCollections.filter((c) => orgsById.has(c.organizationId));
      // One count request per org, shared by all of that org's teams.
      const memberCounts = new Map<string, Promise<Map<string, number> | null>>();

      // Read the cipher cache once for every team's shared-account count.
      const accountsInTeam = await this.loadTeamAccountsIndex();
      if (seq !== this.loadSeq) {
        return;
      }

      const rows = await Promise.all(
        relevant.map(async (collection) => {
          const org = orgsById.get(collection.organizationId)!;
          const memberCount = await this.getMemberCount(org.id, collection.id, memberCounts);
          const sharedAccountsCount = accountsInTeam(org.id, collection.id).length;
          return {
            collectionId: collection.id,
            org,
            name: collection.name,
            subtitle:
              memberCount <= 1
                ? this.i18nService.t("justYou")
                : this.i18nService.t("membersCount", String(memberCount)),
            sharedLabel:
              sharedAccountsCount === 0
                ? this.i18nService.t("noSharesYet")
                : this.i18nService.t("accountsSharedCount", String(sharedAccountsCount)),
            badgeKey: org.isOwner ? "owner" : "member",
          } as TeamRow;
        }),
      );

      if (seq !== this.loadSeq) {
        return; // superseded by a load for a different org list
      }
      this.teamRows = rows;
    } catch (e) {
      if (seq !== this.loadSeq) {
        return;
      }
      this.logService.error(String(e));
      this.platformUtilsService.showToast("error", "", this.i18nService.t("unexpectedError"));
    } finally {
      if (seq === this.loadSeq) {
        this.isLoadingTeams = false;
      }
    }
  }

  // Confirmed members only, counted by the backend so every member (not just those allowed to
  // list the org's users) sees the real number: someone invited who hasn't joined yet isn't
  // counted. Falls back to 1 (the viewer) only if the request itself fails.
  private async getMemberCount(
    orgId: string,
    collectionId: string,
    memberCounts: Map<string, Promise<Map<string, number> | null>>,
  ): Promise<number> {
    if (!memberCounts.has(orgId)) {
      memberCounts.set(
        orgId,
        this.teamInvitationService.getTeamMemberCounts(orgId).catch((e) => {
          this.logService.error(e instanceof Error ? e.message : String(e));
          return null;
        }),
      );
    }
    const counts = await memberCounts.get(orgId)!;
    return Math.max(1, counts?.get(collectionId) ?? 1);
  }

  // Shared-account lookup for this refresh; counts fall back to 0 if the ciphers can't be read.
  private async loadTeamAccountsIndex(): Promise<TeamAccountsLookup> {
    try {
      return await buildTeamAccountsLookup(
        this.cipherService,
        this.userStore.accounts.accountViews(),
      );
    } catch (e) {
      this.logService.error(e instanceof Error ? e.message : String(e));
      return () => [];
    }
  }
}
